import React, { useState, useEffect, useRef } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { apiService } from '../services/api';
import { useBusiness } from '../contexts/BusinessContext';
import { useAuth } from '../hooks/useAuth';
import { X, Plus, Edit, Trash2, Save, X as Cancel, ChevronDown, ArrowLeft, GripVertical, Search, Settings, RotateCcw } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import NavigationBar from '../components/NavigationBar';
import PermissionGate from '../components/PermissionGate';
import CustomAlertModal from '../components/CustomAlertModal';
import BusinessDashboardSidebar from '../components/BusinessDashboardSidebar';

interface Category {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  display_order?: number;
  is_active?: boolean;
  background_color?: string | null;
}

const CATEGORY_BACKGROUND_COLORS: Array<{ name: string; value: string }> = [
  { name: 'Slate', value: '#F1F5F9' },
  { name: 'Blue', value: '#DBEAFE' },
  { name: 'Indigo', value: '#E0E7FF' },
  { name: 'Violet', value: '#EDE9FE' },
  { name: 'Teal', value: '#CCFBF1' },
  { name: 'Emerald', value: '#D1FAE5' },
  { name: 'Amber', value: '#FEF3C7' },
  { name: 'Orange', value: '#FFEDD5' },
  { name: 'Rose', value: '#FFE4E6' }
];

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const normalized = hex.trim();
  const match = /^#([0-9a-fA-F]{6})$/.exec(normalized);
  if (!match) return null;
  const intVal = parseInt(match[1], 16);
  return {
    r: (intVal >> 16) & 255,
    g: (intVal >> 8) & 255,
    b: intVal & 255
  };
}

// Grouped emoji icons with headers and searchable descriptions
const GROUPED_EMOJI_ICONS = [
  {
    title: "Beauty & Wellness",
    icons: [
      { emoji: '💇‍♀️', name: 'woman haircut', keywords: 'haircut, beauty, salon, woman, female' },
      { emoji: '💇‍♂️', name: 'man haircut', keywords: 'haircut, beauty, salon, man, male' },
      { emoji: '💅', name: 'nail polish', keywords: 'nail, polish, beauty, manicure, hands' },
      { emoji: '💄', name: 'lipstick', keywords: 'lipstick, makeup, beauty, cosmetics' },
      { emoji: '🧴', name: 'lotion bottle', keywords: 'lotion, cream, moisturizer, skincare' },
      { emoji: '🛁', name: 'bathtub', keywords: 'bath, tub, relaxation, spa, soak' },
      { emoji: '🧖‍♀️', name: 'woman in steamy room', keywords: 'sauna, steam, spa, woman, relaxation' },
      { emoji: '🧖‍♂️', name: 'man in steamy room', keywords: 'sauna, steam, spa, man, relaxation' },
      { emoji: '💆‍♀️', name: 'woman getting massage', keywords: 'massage, spa, woman, relaxation, therapy' },
      { emoji: '💆‍♂️', name: 'man getting massage', keywords: 'massage, spa, man, relaxation, therapy' },
      { emoji: '🧘‍♀️', name: 'woman in lotus position', keywords: 'yoga, meditation, woman, wellness, zen' },
      { emoji: '🧘‍♂️', name: 'man in lotus position', keywords: 'yoga, meditation, man, wellness, zen' },
      { emoji: '🏃‍♀️', name: 'woman running', keywords: 'running, exercise, fitness, woman, cardio' },
      { emoji: '🏃‍♂️', name: 'man running', keywords: 'running, exercise, fitness, man, cardio' },
      { emoji: '🚶‍♀️', name: 'woman walking', keywords: 'walking, exercise, fitness, woman, stroll' },
      { emoji: '🚶‍♂️', name: 'man walking', keywords: 'walking, exercise, fitness, man, stroll' },
      { emoji: '🏊‍♀️', name: 'woman swimming', keywords: 'swimming, pool, exercise, woman, water' },
      { emoji: '🏊‍♂️', name: 'man swimming', keywords: 'swimming, pool, exercise, man, water' },
      { emoji: '🚴‍♀️', name: 'woman biking', keywords: 'cycling, bike, exercise, woman, outdoor' },
      { emoji: '🚴‍♂️', name: 'man biking', keywords: 'cycling, bike, exercise, man, outdoor' },
      { emoji: '🏋️‍♀️', name: 'woman lifting weights', keywords: 'weights, gym, strength, woman, fitness' },
      { emoji: '🏋️‍♂️', name: 'man lifting weights', keywords: 'weights, gym, strength, man, fitness' },
      { emoji: '💪', name: 'flexed biceps', keywords: 'muscle, strength, power, fitness, arm' },
      { emoji: '🦵', name: 'leg', keywords: 'leg, body, anatomy, limb' },
      { emoji: '🦶', name: 'foot', keywords: 'foot, body, anatomy, toes' },
      { emoji: '👂', name: 'ear', keywords: 'ear, body, anatomy, hearing' },
      { emoji: '👃', name: 'nose', keywords: 'nose, body, anatomy, smell' },
      { emoji: '👄', name: 'mouth', keywords: 'mouth, body, anatomy, lips' },
      { emoji: '👅', name: 'tongue', keywords: 'tongue, body, anatomy, taste' },
      { emoji: '👁️', name: 'eye', keywords: 'eye, body, anatomy, vision, sight' },
      { emoji: '👀', name: 'eyes', keywords: 'eyes, body, anatomy, vision, sight' },
      { emoji: '🧠', name: 'brain', keywords: 'brain, mind, intelligence, thinking, mental' },
      { emoji: '🫀', name: 'anatomical heart', keywords: 'heart, anatomy, organ, health, medical' },
      { emoji: '🫁', name: 'lungs', keywords: 'lungs, anatomy, organ, breathing, respiratory' },
      { emoji: '🦴', name: 'bone', keywords: 'bone, skeleton, anatomy, structure' },
      { emoji: '🦷', name: 'tooth', keywords: 'tooth, teeth, dental, mouth, health' },
      { emoji: '🦿', name: 'mechanical leg', keywords: 'prosthetic, artificial, limb, disability' },
      { emoji: '🦾', name: 'mechanical arm', keywords: 'prosthetic, artificial, limb, disability' },
      { emoji: '💋', name: 'kiss mark', keywords: 'kiss, love, romance, lips, affection' }
    ]
  },
  {
    title: "Health & Medical",
    icons: [
      { emoji: '🏥', name: 'hospital', keywords: 'hospital, medical, health, emergency, care' },
      { emoji: '⚕️', name: 'medical symbol', keywords: 'medical, health, symbol, caduceus, doctor' },
      { emoji: '🩺', name: 'stethoscope', keywords: 'stethoscope, doctor, medical, health, diagnosis' },
      { emoji: '💊', name: 'pill', keywords: 'pill, medicine, medication, health, treatment' },
      { emoji: '🩹', name: 'adhesive bandage', keywords: 'bandage, wound, injury, medical, healing' },
      { emoji: '🩻', name: 'x-ray', keywords: 'x-ray, medical, diagnosis, bones, radiology' },
      { emoji: '🧬', name: 'dna', keywords: 'dna, genetics, science, biology, medical' },
      { emoji: '🔬', name: 'microscope', keywords: 'microscope, science, research, laboratory, medical' },
      { emoji: '🧪', name: 'test tube', keywords: 'test tube, science, laboratory, chemistry, medical' },
      { emoji: '💉', name: 'syringe', keywords: 'syringe, injection, medical, vaccine, needle' },
      { emoji: '🏨', name: 'hotel', keywords: 'hotel, accommodation, travel, building' },
      { emoji: '🏩', name: 'love hotel', keywords: 'love hotel, accommodation, building' },
      { emoji: '🏪', name: 'convenience store', keywords: 'convenience store, shop, retail, building' },
      { emoji: '🏫', name: 'school', keywords: 'school, education, learning, building' },
      { emoji: '🏬', name: 'department store', keywords: 'department store, shopping, retail, building' },
      { emoji: '🏭', name: 'factory', keywords: 'factory, industrial, manufacturing, building' },
      { emoji: '🏯', name: 'japanese castle', keywords: 'japanese castle, building, historical, architecture' },
      { emoji: '🏰', name: 'castle', keywords: 'castle, building, historical, architecture, medieval' },
      { emoji: '💒', name: 'wedding', keywords: 'wedding, marriage, ceremony, celebration' },
      { emoji: '⛪', name: 'church', keywords: 'church, religion, christian, building, worship' },
      { emoji: '🕌', name: 'mosque', keywords: 'mosque, religion, islam, building, worship' },
      { emoji: '🛕', name: 'hindu temple', keywords: 'hindu temple, religion, building, worship' },
      { emoji: '🕍', name: 'synagogue', keywords: 'synagogue, religion, judaism, building, worship' },
      { emoji: '⛩️', name: 'shinto shrine', keywords: 'shinto shrine, religion, japan, building, worship' },
      { emoji: '🕋', name: 'kaaba', keywords: 'kaaba, mecca, islam, religion, building' },
      { emoji: '⛲', name: 'fountain', keywords: 'fountain, water, decoration, park, public' },
      { emoji: '⛺', name: 'tent', keywords: 'tent, camping, outdoor, shelter, temporary' },
      { emoji: '🌁', name: 'foggy', keywords: 'foggy, weather, mist, cloudy, atmosphere' },
      { emoji: '🌃', name: 'night with stars', keywords: 'night, stars, sky, evening, dark' },
      { emoji: '🏙️', name: 'cityscape', keywords: 'cityscape, urban, buildings, skyline, city' },
      { emoji: '🌄', name: 'sunrise over mountains', keywords: 'sunrise, mountains, morning, nature, landscape' },
      { emoji: '🌅', name: 'sunrise', keywords: 'sunrise, morning, dawn, sky, horizon' },
      { emoji: '🌆', name: 'cityscape at dusk', keywords: 'cityscape, dusk, evening, urban, sunset' },
      { emoji: '🌇', name: 'sunset', keywords: 'sunset, evening, dusk, sky, horizon' },
      { emoji: '🌉', name: 'bridge at night', keywords: 'bridge, night, city, urban, architecture' },
      { emoji: '♨️', name: 'hot springs', keywords: 'hot springs, spa, relaxation, japan, thermal' },
      { emoji: '🎠', name: 'carousel horse', keywords: 'carousel, amusement park, ride, horse, fun' },
      { emoji: '🎡', name: 'ferris wheel', keywords: 'ferris wheel, amusement park, ride, carnival, fun' },
      { emoji: '🎢', name: 'roller coaster', keywords: 'roller coaster, amusement park, ride, thrill, fun' },
      { emoji: '💈', name: 'barber pole', keywords: 'barber pole, haircut, salon, traditional, sign' },
      { emoji: '🎪', name: 'circus tent', keywords: 'circus tent, entertainment, show, carnival, performance' }
    ]
  },
  {
    title: "Tools & Services",
    icons: [
      { emoji: '🛠️', name: 'wrench and hammer', keywords: 'tools, repair, maintenance, construction, hardware' },
      { emoji: '🔧', name: 'wrench', keywords: 'wrench, tool, repair, maintenance, mechanical' },
      { emoji: '🔨', name: 'hammer', keywords: 'hammer, tool, construction, building, nail' },
      { emoji: '⚙️', name: 'gear', keywords: 'gear, mechanical, engineering, settings, configuration' },
      { emoji: '🔩', name: 'nut and bolt', keywords: 'nut, bolt, fastener, hardware, mechanical' },
      { emoji: '🪛', name: 'screwdriver', keywords: 'screwdriver, tool, repair, maintenance, diy' },
      { emoji: '🪜', name: 'ladder', keywords: 'ladder, climbing, construction, height, access' },
      { emoji: '🪣', name: 'bucket', keywords: 'bucket, container, cleaning, storage, water' },
      { emoji: '🧰', name: 'toolbox', keywords: 'toolbox, tools, repair, maintenance, storage' },
      { emoji: '📦', name: 'package', keywords: 'package, box, delivery, shipping, storage' },
      { emoji: '📋', name: 'clipboard', keywords: 'clipboard, document, notes, checklist, office' },
      { emoji: '📁', name: 'file folder', keywords: 'folder, file, document, organization, office' },
      { emoji: '📂', name: 'open file folder', keywords: 'folder, file, document, open, office' },
      { emoji: '🗂️', name: 'card index dividers', keywords: 'dividers, organization, cards, index, office' },
      { emoji: '📅', name: 'calendar', keywords: 'calendar, date, schedule, planning, time' },
      { emoji: '📆', name: 'tear-off calendar', keywords: 'calendar, date, tear-off, schedule, time' },
      { emoji: '🗓️', name: 'spiral calendar', keywords: 'calendar, spiral, date, schedule, time' },
      { emoji: '📇', name: 'card index', keywords: 'card, index, contact, business, office' },
      { emoji: '🗃️', name: 'card file box', keywords: 'file box, cards, storage, organization, office' },
      { emoji: '📌', name: 'pushpin', keywords: 'pushpin, pin, attach, notice, board' },
      { emoji: '📍', name: 'round pushpin', keywords: 'pushpin, pin, location, map, marker' },
      { emoji: '📎', name: 'paperclip', keywords: 'paperclip, attach, document, office, fasten' },
      { emoji: '🖇️', name: 'linked paperclips', keywords: 'paperclips, linked, attach, document, office' },
      { emoji: '📏', name: 'straight ruler', keywords: 'ruler, measure, straight, length, tool' },
      { emoji: '📐', name: 'triangular ruler', keywords: 'ruler, triangle, measure, angle, tool' },
      { emoji: '✂️', name: 'scissors', keywords: 'scissors, cut, tool, craft, office' },
      { emoji: '🗄️', name: 'file cabinet', keywords: 'file cabinet, storage, office, organization, furniture' },
      { emoji: '🗑️', name: 'wastebasket', keywords: 'wastebasket, trash, delete, garbage, office' },
      { emoji: '🔒', name: 'locked', keywords: 'lock, security, private, protect, safe' },
      { emoji: '🔓', name: 'unlocked', keywords: 'unlock, open, access, free, available' },
      { emoji: '🔏', name: 'locked with pen', keywords: 'lock, pen, security, private, protect' },
      { emoji: '🔐', name: 'locked with key', keywords: 'lock, key, security, private, protect' },
      { emoji: '🔑', name: 'key', keywords: 'key, unlock, access, door, security' },
      { emoji: '🗝️', name: 'old key', keywords: 'old key, antique, unlock, access, vintage' }
    ]
  },
  {
    title: "Technology",
    icons: [
      { emoji: '💻', name: 'laptop', keywords: 'laptop, computer, technology, work, portable' },
      { emoji: '🖥️', name: 'desktop computer', keywords: 'desktop, computer, technology, work, monitor' },
      { emoji: '📱', name: 'mobile phone', keywords: 'phone, mobile, smartphone, communication, technology' },
      { emoji: '⌨️', name: 'keyboard', keywords: 'keyboard, computer, input, typing, technology' },
      { emoji: '🖱️', name: 'computer mouse', keywords: 'mouse, computer, cursor, click, technology' },
      { emoji: '🖨️', name: 'printer', keywords: 'printer, print, document, office, technology' },
      { emoji: '📡', name: 'satellite antenna', keywords: 'satellite, antenna, signal, communication, technology' },
      { emoji: '🔌', name: 'electric plug', keywords: 'plug, electricity, power, charge, technology' },
      { emoji: '💡', name: 'light bulb', keywords: 'light bulb, idea, innovation, bright, technology' },
      { emoji: '🔋', name: 'battery', keywords: 'battery, power, energy, charge, technology' },
      { emoji: '📺', name: 'television', keywords: 'tv, television, entertainment, screen, technology' },
      { emoji: '📷', name: 'camera', keywords: 'camera, photo, photography, capture, technology' },
      { emoji: '📹', name: 'video camera', keywords: 'video camera, recording, film, capture, technology' },
      { emoji: '📼', name: 'videocassette', keywords: 'videocassette, tape, video, recording, retro' },
      { emoji: '🔍', name: 'magnifying glass tilted left', keywords: 'search, magnify, zoom, find, tool' },
      { emoji: '🔎', name: 'magnifying glass tilted right', keywords: 'search, magnify, zoom, find, tool' },
      { emoji: '🕯️', name: 'candle', keywords: 'candle, light, flame, decoration, romantic' },
      { emoji: '🔦', name: 'flashlight', keywords: 'flashlight, torch, light, beam, tool' },
      { emoji: '🏮', name: 'red paper lantern', keywords: 'lantern, light, decoration, asian, festival' },
      { emoji: '🪔', name: 'diya lamp', keywords: 'diya, lamp, light, indian, celebration' },
      { emoji: '📞', name: 'telephone receiver', keywords: 'phone, telephone, call, communication, contact' },
      { emoji: '📟', name: 'pager', keywords: 'pager, beeper, communication, retro, business' },
      { emoji: '📠', name: 'fax machine', keywords: 'fax, machine, document, communication, office' },
      { emoji: '📻', name: 'radio', keywords: 'radio, music, broadcast, entertainment, audio' },
      { emoji: '🎙️', name: 'studio microphone', keywords: 'microphone, studio, recording, audio, music' },
      { emoji: '🎚️', name: 'level slider', keywords: 'slider, control, audio, music, adjust' },
      { emoji: '🎛️', name: 'control knobs', keywords: 'knobs, control, audio, music, adjust' },
      { emoji: '🧭', name: 'compass', keywords: 'compass, navigation, direction, travel, orienteering' },
      { emoji: '⏱️', name: 'stopwatch', keywords: 'stopwatch, time, measure, sports, timing' },
      { emoji: '⏲️', name: 'timer clock', keywords: 'timer, clock, countdown, time, alarm' },
      { emoji: '⏰', name: 'alarm clock', keywords: 'alarm, clock, wake, time, morning' },
      { emoji: '🕰️', name: 'mantelpiece clock', keywords: 'clock, time, antique, vintage, decoration' },
      { emoji: '⌛', name: 'hourglass done', keywords: 'hourglass, time, sand, measure, done' },
      { emoji: '⏳', name: 'hourglass not done', keywords: 'hourglass, time, sand, measure, running' },
      { emoji: '🖲️', name: 'trackball', keywords: 'trackball, computer, input, cursor, technology' },
      { emoji: '💽', name: 'computer disk', keywords: 'disk, computer, storage, data, retro' },
      { emoji: '💾', name: 'floppy disk', keywords: 'floppy disk, save, storage, data, retro' },
      { emoji: '💿', name: 'optical disk', keywords: 'cd, disk, music, data, storage' },
      { emoji: '📀', name: 'dvd', keywords: 'dvd, disk, movie, video, storage' }
    ]
  },
  {
    title: "Food & Beverage",
    icons: [
      { emoji: '🍽️', name: 'fork and knife with plate', keywords: 'dining, meal, food, restaurant, eating' },
      { emoji: '🍕', name: 'pizza', keywords: 'pizza, food, italian, cheese, meal' },
      { emoji: '🍔', name: 'hamburger', keywords: 'burger, hamburger, food, fast food, meal' },
      { emoji: '🍜', name: 'steaming bowl', keywords: 'noodles, soup, food, asian, meal' },
      { emoji: '🍣', name: 'sushi', keywords: 'sushi, food, japanese, fish, meal' },
      { emoji: '🍰', name: 'shortcake', keywords: 'cake, dessert, sweet, birthday, celebration' },
      { emoji: '☕', name: 'hot beverage', keywords: 'coffee, tea, hot drink, beverage, morning' },
      { emoji: '🍺', name: 'beer mug', keywords: 'beer, alcohol, drink, beverage, pub' },
      { emoji: '🍷', name: 'wine glass', keywords: 'wine, alcohol, drink, beverage, elegant' },
      { emoji: '🥤', name: 'cup with straw', keywords: 'soda, soft drink, beverage, refreshment' },
      { emoji: '🍹', name: 'tropical drink', keywords: 'cocktail, tropical, drink, beverage, vacation' },
      { emoji: '🍸', name: 'cocktail glass', keywords: 'cocktail, drink, alcohol, beverage, party' },
      { emoji: '🍴', name: 'fork and knife', keywords: 'utensils, dining, food, eating, restaurant' },
      { emoji: '🥄', name: 'spoon', keywords: 'spoon, utensil, eating, food, dining' },
      { emoji: '🔪', name: 'kitchen knife', keywords: 'knife, kitchen, cooking, food, utensil' },
      { emoji: '🏺', name: 'amphora', keywords: 'amphora, vase, ancient, greek, container' },
      { emoji: '🍶', name: 'sake', keywords: 'sake, japanese, alcohol, drink, beverage' },
      { emoji: '🍾', name: 'bottle with popping cork', keywords: 'champagne, celebration, alcohol, drink, party' },
      { emoji: '🍻', name: 'clinking beer mugs', keywords: 'beer, cheers, celebration, drink, friendship' },
      { emoji: '🥂', name: 'clinking glasses', keywords: 'cheers, celebration, toast, drink, party' },
      { emoji: '🥃', name: 'tumbler glass', keywords: 'whiskey, alcohol, drink, beverage, glass' },
      { emoji: '🧃', name: 'beverage box', keywords: 'juice, box, drink, beverage, carton' },
      { emoji: '🥛', name: 'glass of milk', keywords: 'milk, drink, beverage, dairy, healthy' },
      { emoji: '🫖', name: 'teapot', keywords: 'tea, teapot, drink, beverage, british' },
      { emoji: '🧋', name: 'bubble tea', keywords: 'bubble tea, drink, beverage, asian, sweet' },
      { emoji: '🍵', name: 'teacup without handle', keywords: 'tea, cup, drink, beverage, hot' },
      { emoji: '🧉', name: 'mate', keywords: 'mate, tea, drink, beverage, south american' },
      { emoji: '🍼', name: 'baby bottle', keywords: 'baby bottle, milk, infant, feeding, baby' }
    ]
  },
  {
    title: "Education & Learning",
    icons: [
      { emoji: '📚', name: 'books', keywords: 'books, education, learning, study, knowledge' },
      { emoji: '✏️', name: 'pencil', keywords: 'pencil, writing, drawing, school, office' },
      { emoji: '📝', name: 'memo', keywords: 'memo, note, writing, document, paper' },
      { emoji: '🎓', name: 'graduation cap', keywords: 'graduation, degree, education, university, achievement' },
      { emoji: '🏫', name: 'school', keywords: 'school, education, learning, building, institution' },
      { emoji: '📖', name: 'open book', keywords: 'book, reading, literature, education, knowledge' },
      { emoji: '✒️', name: 'black nib', keywords: 'pen, writing, fountain pen, calligraphy, elegant' },
      { emoji: '🖊️', name: 'pen', keywords: 'pen, writing, office, document, signature' },
      { emoji: '📐', name: 'triangular ruler', keywords: 'ruler, triangle, measure, angle, geometry' },
      { emoji: '🧮', name: 'abacus', keywords: 'abacus, calculation, math, counting, traditional' },
      { emoji: '🔢', name: 'input numbers', keywords: 'numbers, input, keypad, calculator, digits' },
      { emoji: '📰', name: 'newspaper', keywords: 'newspaper, news, media, information, current events' },
      { emoji: '📑', name: 'bookmark tabs', keywords: 'bookmarks, tabs, organization, documents, files' },
      { emoji: '🔖', name: 'bookmark', keywords: 'bookmark, save, mark, page, reading' },
      { emoji: '🏷️', name: 'label', keywords: 'label, tag, price, identification, mark' },
      { emoji: '💰', name: 'money bag', keywords: 'money, wealth, cash, finance, currency' },
      { emoji: '🪙', name: 'coin', keywords: 'coin, money, currency, gold, silver' },
      { emoji: '💴', name: 'yen banknote', keywords: 'yen, japanese, money, currency, banknote' },
      { emoji: '💵', name: 'dollar banknote', keywords: 'dollar, money, currency, banknote, cash' },
      { emoji: '💶', name: 'euro banknote', keywords: 'euro, money, currency, banknote, europe' },
      { emoji: '💷', name: 'pound banknote', keywords: 'pound, money, currency, banknote, british' },
      { emoji: '📊', name: 'bar chart', keywords: 'chart, graph, data, statistics, analysis' },
      { emoji: '📈', name: 'chart increasing', keywords: 'chart, graph, growth, increase, trend' },
      { emoji: '📉', name: 'chart decreasing', keywords: 'chart, graph, decline, decrease, trend' },
      { emoji: '🗒️', name: 'spiral notepad', keywords: 'notepad, notes, writing, spiral, paper' }
    ]
  },
  {
    title: "Transportation",
    icons: [
      { emoji: '🚗', name: 'automobile', keywords: 'car, automobile, vehicle, transport, travel' },
      { emoji: '🚕', name: 'taxi', keywords: 'taxi, cab, transportation, travel, service' },
      { emoji: '🚙', name: 'sport utility vehicle', keywords: 'suv, vehicle, car, transportation, travel' },
      { emoji: '🚌', name: 'bus', keywords: 'bus, public transport, transportation, travel, commute' },
      { emoji: '🚎', name: 'trolleybus', keywords: 'trolleybus, bus, public transport, transportation' },
      { emoji: '🏎️', name: 'racing car', keywords: 'racing car, formula one, speed, sport, competition' },
      { emoji: '🚓', name: 'police car', keywords: 'police car, law enforcement, emergency, vehicle' },
      { emoji: '🚑', name: 'ambulance', keywords: 'ambulance, emergency, medical, health, vehicle' },
      { emoji: '🚒', name: 'fire engine', keywords: 'fire engine, fire truck, emergency, rescue, vehicle' },
      { emoji: '🚐', name: 'minibus', keywords: 'minibus, van, transportation, travel, vehicle' },
      { emoji: '🚚', name: 'delivery truck', keywords: 'delivery truck, transport, shipping, logistics, vehicle' },
      { emoji: '🚛', name: 'articulated lorry', keywords: 'truck, lorry, transport, shipping, vehicle' },
      { emoji: '🚜', name: 'tractor', keywords: 'tractor, farm, agriculture, vehicle, machinery' },
      { emoji: '🛴', name: 'kick scooter', keywords: 'scooter, kick scooter, transportation, travel, personal' },
      { emoji: '🚲', name: 'bicycle', keywords: 'bicycle, bike, cycling, transportation, exercise' },
      { emoji: '🛵', name: 'motor scooter', keywords: 'motor scooter, moped, transportation, travel, vehicle' },
      { emoji: '🏍️', name: 'motorcycle', keywords: 'motorcycle, bike, transportation, travel, vehicle' },
      { emoji: '🛺', name: 'auto rickshaw', keywords: 'auto rickshaw, tuk tuk, transportation, travel, asia' },
      { emoji: '🚨', name: 'rotating light', keywords: 'emergency light, police, ambulance, warning, alert' },
      { emoji: '🚔', name: 'oncoming police car', keywords: 'police car, law enforcement, emergency, vehicle' },
      { emoji: '🚍', name: 'oncoming bus', keywords: 'bus, public transport, transportation, travel' },
      { emoji: '🚘', name: 'oncoming automobile', keywords: 'car, automobile, vehicle, transport, travel' },
      { emoji: '🚖', name: 'oncoming taxi', keywords: 'taxi, cab, transportation, travel, service' },
      { emoji: '🚡', name: 'aerial tramway', keywords: 'cable car, tramway, transportation, travel, mountain' },
      { emoji: '🚠', name: 'mountain cableway', keywords: 'cableway, mountain, transportation, travel, ski' },
      { emoji: '🚟', name: 'suspension railway', keywords: 'suspension railway, monorail, transportation, travel' },
      { emoji: '🚃', name: 'railway car', keywords: 'train car, railway, transportation, travel, commute' },
      { emoji: '🚋', name: 'tram car', keywords: 'tram, streetcar, transportation, travel, public transport' },
      { emoji: '🚞', name: 'mountain railway', keywords: 'mountain railway, train, transportation, travel' },
      { emoji: '🚝', name: 'monorail', keywords: 'monorail, train, transportation, travel, modern' },
      { emoji: '🚄', name: 'high-speed train', keywords: 'bullet train, high speed, transportation, travel, fast' },
      { emoji: '🚅', name: 'high-speed train with bullet nose', keywords: 'bullet train, high speed, transportation, travel, fast' },
      { emoji: '🚈', name: 'light rail', keywords: 'light rail, train, transportation, travel, urban' },
      { emoji: '🚂', name: 'locomotive', keywords: 'train, locomotive, transportation, travel, steam' },
      { emoji: '🚆', name: 'train', keywords: 'train, railway, transportation, travel, commute' },
      { emoji: '🚇', name: 'metro', keywords: 'subway, metro, underground, transportation, travel' },
      { emoji: '🚊', name: 'tram', keywords: 'tram, streetcar, transportation, travel, public transport' },
      { emoji: '🚉', name: 'station', keywords: 'train station, transportation, travel, platform, commute' },
      { emoji: '✈️', name: 'airplane', keywords: 'airplane, plane, flight, travel, transportation' },
      { emoji: '🛫', name: 'airplane departure', keywords: 'airplane departure, flight, travel, takeoff, airport' },
      { emoji: '🛬', name: 'airplane arrival', keywords: 'airplane arrival, flight, travel, landing, airport' },
      { emoji: '🛩️', name: 'small airplane', keywords: 'small airplane, plane, flight, travel, private' },
      { emoji: '💺', name: 'seat', keywords: 'seat, chair, sitting, comfort, travel' },
      { emoji: '🛰️', name: 'satellite', keywords: 'satellite, space, technology, communication, orbit' },
      { emoji: '🚀', name: 'rocket', keywords: 'rocket, space, launch, technology, exploration' },
      { emoji: '🛸', name: 'flying saucer', keywords: 'ufo, flying saucer, space, alien, science fiction' },
      { emoji: '🚁', name: 'helicopter', keywords: 'helicopter, aircraft, transportation, travel, rescue' },
      { emoji: '🛶', name: 'canoe', keywords: 'canoe, boat, water, transportation, travel, paddle' }
    ]
  },
  {
    title: "Entertainment",
    icons: [
      { emoji: '🎬', name: 'clapper board', keywords: 'movie, film, cinema, entertainment, production' },
      { emoji: '🎭', name: 'performing arts', keywords: 'theater, drama, performance, entertainment, arts' },
      { emoji: '🎨', name: 'artist palette', keywords: 'art, painting, creativity, artist, palette' },
      { emoji: '🎪', name: 'circus tent', keywords: 'circus, entertainment, show, carnival, performance' },
      { emoji: '🎯', name: 'direct hit', keywords: 'target, aim, precision, game, darts' },
      { emoji: '🎲', name: 'game die', keywords: 'dice, game, gambling, chance, luck' },
      { emoji: '🎮', name: 'video game', keywords: 'video game, gaming, entertainment, console, play' },
      { emoji: '🎸', name: 'guitar', keywords: 'guitar, music, instrument, rock, string' },
      { emoji: '🎹', name: 'musical keyboard', keywords: 'piano, keyboard, music, instrument, keys' },
      { emoji: '🎺', name: 'trumpet', keywords: 'trumpet, music, instrument, brass, horn' },
      { emoji: '🎻', name: 'violin', keywords: 'violin, music, instrument, string, classical' },
      { emoji: '🥁', name: 'drum', keywords: 'drum, music, instrument, percussion, beat' },
      { emoji: '🎤', name: 'microphone', keywords: 'microphone, music, singing, performance, audio' },
      { emoji: '🎧', name: 'headphone', keywords: 'headphone, music, audio, listening, sound' },
      { emoji: '🎼', name: 'musical score', keywords: 'music score, sheet music, composition, notes' },
      { emoji: '🎵', name: 'musical note', keywords: 'musical note, music, sound, melody, tune' },
      { emoji: '🎶', name: 'musical notes', keywords: 'musical notes, music, melody, harmony, tune' }
    ]
  },
  {
    title: "Business & Finance",
    icons: [
      { emoji: '💼', name: 'briefcase', keywords: 'briefcase, business, work, office, professional' },
      { emoji: '💳', name: 'credit card', keywords: 'credit card, payment, money, finance, plastic' },
      { emoji: '🏦', name: 'bank', keywords: 'bank, finance, money, institution, building' },
      { emoji: '💱', name: 'currency exchange', keywords: 'currency exchange, money, finance, conversion' },
      { emoji: '💲', name: 'heavy dollar sign', keywords: 'dollar sign, money, currency, finance, wealth' },
      { emoji: '🏢', name: 'office building', keywords: 'office building, business, work, corporate, building' },
      { emoji: '📞', name: 'telephone receiver', keywords: 'phone, telephone, call, communication, contact' },
      { emoji: '📧', name: 'e-mail', keywords: 'email, mail, communication, message, digital' },
      { emoji: '📨', name: 'incoming envelope', keywords: 'envelope, mail, letter, communication, incoming' },
      { emoji: '📩', name: 'envelope with arrow', keywords: 'envelope, mail, arrow, communication, outgoing' },
      { emoji: '📪', name: 'closed mailbox with lowered flag', keywords: 'mailbox, mail, post, communication, closed' },
      { emoji: '📫', name: 'closed mailbox with raised flag', keywords: 'mailbox, mail, post, communication, flag' },
      { emoji: '📬', name: 'open mailbox with raised flag', keywords: 'mailbox, mail, post, communication, open' },
      { emoji: '📭', name: 'open mailbox with lowered flag', keywords: 'mailbox, mail, post, communication, empty' },
      { emoji: '📮', name: 'postbox', keywords: 'postbox, mail, post, communication, public' },
      { emoji: '🗳️', name: 'ballot box with ballot', keywords: 'ballot box, voting, election, democracy, ballot' }
    ]
  },
  {
    title: "Home & Garden",
    icons: [
      { emoji: '🏠', name: 'house', keywords: 'house, home, building, residence, dwelling' },
      { emoji: '🏡', name: 'house with garden', keywords: 'house with garden, home, building, residence, yard' },
      { emoji: '🏘️', name: 'houses', keywords: 'houses, neighborhood, residential, buildings, community' },
      { emoji: '🏚️', name: 'derelict house', keywords: 'derelict house, abandoned, old, building, run-down' },
      { emoji: '🏗️', name: 'building construction', keywords: 'building construction, construction, building, work, development' },
      { emoji: '🏭', name: 'factory', keywords: 'factory, industrial, manufacturing, building, production' },
      { emoji: '🏢', name: 'office building', keywords: 'office building, business, work, corporate, building' },
      { emoji: '🏬', name: 'department store', keywords: 'department store, shopping, retail, building, store' },
      { emoji: '🏣', name: 'japanese post office', keywords: 'japanese post office, post office, mail, japan, building' },
      { emoji: '🏤', name: 'post office', keywords: 'post office, mail, postal, building, service' },
      { emoji: '🏥', name: 'hospital', keywords: 'hospital, medical, health, emergency, care' },
      { emoji: '🏨', name: 'hotel', keywords: 'hotel, accommodation, travel, building, stay' },
      { emoji: '🏩', name: 'love hotel', keywords: 'love hotel, accommodation, building, romantic' },
      { emoji: '🏪', name: 'convenience store', keywords: 'convenience store, shop, retail, building, store' },
      { emoji: '🏫', name: 'school', keywords: 'school, education, learning, building, institution' },
      { emoji: '🏯', name: 'japanese castle', keywords: 'japanese castle, building, historical, architecture, japan' },
      { emoji: '🏰', name: 'castle', keywords: 'castle, building, historical, architecture, medieval' },
      { emoji: '💒', name: 'wedding', keywords: 'wedding, marriage, ceremony, celebration, love' },
      { emoji: '⛪', name: 'church', keywords: 'church, religion, christian, building, worship' },
      { emoji: '🕌', name: 'mosque', keywords: 'mosque, religion, islam, building, worship' },
      { emoji: '🛕', name: 'hindu temple', keywords: 'hindu temple, religion, building, worship, india' },
      { emoji: '🕍', name: 'synagogue', keywords: 'synagogue, religion, judaism, building, worship' },
      { emoji: '⛩️', name: 'shinto shrine', keywords: 'shinto shrine, religion, japan, building, worship' },
      { emoji: '🕋', name: 'kaaba', keywords: 'kaaba, mecca, islam, religion, building' },
      { emoji: '⛲', name: 'fountain', keywords: 'fountain, water, decoration, park, public' },
      { emoji: '⛺', name: 'tent', keywords: 'tent, camping, outdoor, shelter, temporary' },
      { emoji: '🌁', name: 'foggy', keywords: 'foggy, weather, mist, cloudy, atmosphere' },
      { emoji: '🌃', name: 'night with stars', keywords: 'night, stars, sky, evening, dark' },
      { emoji: '🏙️', name: 'cityscape', keywords: 'cityscape, urban, buildings, skyline, city' },
      { emoji: '🌄', name: 'sunrise over mountains', keywords: 'sunrise, mountains, morning, nature, landscape' },
      { emoji: '🌅', name: 'sunrise', keywords: 'sunrise, morning, dawn, sky, horizon' },
      { emoji: '🌆', name: 'cityscape at dusk', keywords: 'cityscape, dusk, evening, urban, sunset' },
      { emoji: '🌇', name: 'sunset', keywords: 'sunset, evening, dusk, sky, horizon' },
      { emoji: '🌉', name: 'bridge at night', keywords: 'bridge, night, city, urban, architecture' },
      { emoji: '♨️', name: 'hot springs', keywords: 'hot springs, spa, relaxation, japan, thermal' }
    ]
  },
  {
    title: "Sports & Fitness",
    icons: [
      { emoji: '⚽', name: 'soccer ball', keywords: 'soccer, football, sports, ball, game' },
      { emoji: '🏀', name: 'basketball', keywords: 'basketball, sports, ball, game, hoop' },
      { emoji: '🏈', name: 'american football', keywords: 'football, american football, sports, ball, game' },
      { emoji: '⚾', name: 'baseball', keywords: 'baseball, sports, ball, game, bat' },
      { emoji: '🎾', name: 'tennis', keywords: 'tennis, sports, ball, game, racket' },
      { emoji: '🏐', name: 'volleyball', keywords: 'volleyball, sports, ball, game, net' },
      { emoji: '🏉', name: 'rugby football', keywords: 'rugby, football, sports, ball, game' },
      { emoji: '🎱', name: 'pool 8 ball', keywords: 'pool, billiards, sports, ball, game' },
      { emoji: '🏓', name: 'ping pong', keywords: 'ping pong, table tennis, sports, ball, game' },
      { emoji: '🏸', name: 'badminton', keywords: 'badminton, sports, shuttlecock, game, racket' },
      { emoji: '🏄‍♀️', name: 'woman surfing', keywords: 'surfing, water sports, woman, ocean, wave' },
      { emoji: '🏄‍♂️', name: 'man surfing', keywords: 'surfing, water sports, man, ocean, wave' },
      { emoji: '🚣‍♀️', name: 'woman rowing boat', keywords: 'rowing, boat, water sports, woman, oar' },
      { emoji: '🚣‍♂️', name: 'man rowing boat', keywords: 'rowing, boat, water sports, man, oar' },
      { emoji: '⛹️‍♀️', name: 'woman bouncing ball', keywords: 'basketball, sports, woman, ball, bounce' },
      { emoji: '⛹️‍♂️', name: 'man bouncing ball', keywords: 'basketball, sports, man, ball, bounce' },
      { emoji: '🚵‍♀️', name: 'woman mountain biking', keywords: 'mountain biking, cycling, sports, woman, bike' },
      { emoji: '🚵‍♂️', name: 'man mountain biking', keywords: 'mountain biking, cycling, sports, man, bike' },
      { emoji: '🏇', name: 'horse racing', keywords: 'horse racing, sports, horse, jockey, race' },
      { emoji: '🛷️', name: 'sled', keywords: 'sled, winter sports, snow, sliding, toboggan' },
      { emoji: '⛷️', name: 'skier', keywords: 'skiing, winter sports, snow, mountain, slope' },
      { emoji: '🏂', name: 'snowboarder', keywords: 'snowboarding, winter sports, snow, mountain, board' },
      { emoji: '🏌️‍♀️', name: 'woman golfing', keywords: 'golf, sports, woman, club, course' },
      { emoji: '🏌️‍♂️', name: 'man golfing', keywords: 'golf, sports, man, club, course' }
    ]
  },
  {
    title: "Nature & Outdoors",
    icons: [
      { emoji: '🌲', name: 'evergreen tree', keywords: 'tree, evergreen, nature, forest, pine' },
      { emoji: '🌳', name: 'deciduous tree', keywords: 'tree, deciduous, nature, forest, oak' },
      { emoji: '🌴', name: 'palm tree', keywords: 'palm tree, tropical, nature, beach, vacation' },
      { emoji: '🌵', name: 'cactus', keywords: 'cactus, desert, nature, plant, arid' },
      { emoji: '🌾', name: 'sheaf of rice', keywords: 'rice, grain, nature, agriculture, harvest' },
      { emoji: '🌿', name: 'herb', keywords: 'herb, plant, nature, green, leaf' },
      { emoji: '☘️', name: 'shamrock', keywords: 'shamrock, clover, nature, ireland, luck' },
      { emoji: '🍀', name: 'four leaf clover', keywords: 'four leaf clover, nature, luck, green, leaf' },
      { emoji: '🍁', name: 'maple leaf', keywords: 'maple leaf, nature, autumn, fall, canada' },
      { emoji: '🍂', name: 'fallen leaf', keywords: 'fallen leaf, nature, autumn, fall, ground' },
      { emoji: '🍃', name: 'leaf fluttering in wind', keywords: 'leaf, nature, wind, movement, green' },
      { emoji: '🌺', name: 'hibiscus', keywords: 'hibiscus, flower, nature, tropical, beauty' },
      { emoji: '🌸', name: 'cherry blossom', keywords: 'cherry blossom, flower, nature, spring, japan' },
      { emoji: '💮', name: 'white flower', keywords: 'white flower, nature, beauty, purity, blossom' },
      { emoji: '🏵️', name: 'rosette', keywords: 'rosette, flower, nature, decoration, award' },
      { emoji: '🌹', name: 'rose', keywords: 'rose, flower, nature, love, beauty' },
      { emoji: '🥀', name: 'wilted flower', keywords: 'wilted flower, nature, sad, dying, faded' },
      { emoji: '🌻', name: 'sunflower', keywords: 'sunflower, flower, nature, yellow, summer' },
      { emoji: '🌼', name: 'daisy', keywords: 'daisy, flower, nature, white, simple' },
      { emoji: '🌷', name: 'tulip', keywords: 'tulip, flower, nature, spring, holland' },
      { emoji: '🌱', name: 'seedling', keywords: 'seedling, plant, nature, growth, new' }
    ]
  },
  {
    title: "Animals & Pets",
    icons: [
      { emoji: '🐕', name: 'dog', keywords: 'dog, pet, animal, canine, loyal' },
      { emoji: '🐈', name: 'cat', keywords: 'cat, pet, animal, feline, independent' },
      { emoji: '🐎', name: 'horse', keywords: 'horse, animal, equine, riding, farm' },
      { emoji: '🐄', name: 'cow', keywords: 'cow, animal, bovine, farm, dairy' },
      { emoji: '🐖', name: 'pig', keywords: 'pig, animal, swine, farm, pork' },
      { emoji: '🐑', name: 'sheep', keywords: 'sheep, animal, ovine, farm, wool' },
      { emoji: '🐐', name: 'goat', keywords: 'goat, animal, caprine, farm, milk' },
      { emoji: '🐓', name: 'rooster', keywords: 'rooster, chicken, animal, bird, farm' },
      { emoji: '🦆', name: 'duck', keywords: 'duck, animal, bird, water, quack' },
      { emoji: '🦅', name: 'eagle', keywords: 'eagle, bird, raptor, predator, majestic' },
      { emoji: '🦉', name: 'owl', keywords: 'owl, bird, nocturnal, wise, predator' },
      { emoji: '🦇', name: 'bat', keywords: 'bat, animal, nocturnal, flying, mammal' },
      { emoji: '🐺', name: 'wolf', keywords: 'wolf, animal, canine, wild, pack' },
      { emoji: '🐱', name: 'cat face', keywords: 'cat, pet, animal, feline, cute' },
      { emoji: '🐈‍⬛', name: 'black cat', keywords: 'black cat, pet, animal, feline, halloween' },
      { emoji: '🐅', name: 'tiger', keywords: 'tiger, animal, feline, wild, stripes' },
      { emoji: '🐆', name: 'leopard', keywords: 'leopard, animal, feline, wild, spots' },
      { emoji: '🐴', name: 'horse face', keywords: 'horse, animal, equine, face, farm' },
      { emoji: '🫎', name: 'moose', keywords: 'moose, animal, deer, antlers, forest' },
      { emoji: '🫏', name: 'donkey', keywords: 'donkey, animal, equine, farm, stubborn' },
      { emoji: '🐂', name: 'ox', keywords: 'ox, animal, bovine, farm, strong' },
      { emoji: '🐃', name: 'water buffalo', keywords: 'water buffalo, animal, bovine, farm, asia' },
      { emoji: '🐷', name: 'pig face', keywords: 'pig, animal, swine, farm, cute' },
      { emoji: '🐗', name: 'boar', keywords: 'boar, animal, swine, wild, tusks' },
      { emoji: '🐽', name: 'pig nose', keywords: 'pig nose, animal, swine, snout' },
      { emoji: '🐏', name: 'ram', keywords: 'ram, sheep, animal, male, horns' },
      { emoji: '🐪', name: 'camel', keywords: 'camel, animal, desert, hump, transportation' },
      { emoji: '🐫', name: 'two-hump camel', keywords: 'camel, animal, desert, humps, asia' },
      { emoji: '🦙', name: 'llama', keywords: 'llama, animal, south america, wool, pack' },
      { emoji: '🦒', name: 'giraffe', keywords: 'giraffe, animal, africa, tall, neck' },
      { emoji: '🐘', name: 'elephant', keywords: 'elephant, animal, africa, trunk, large' },
      { emoji: '🦣', name: 'mammoth', keywords: 'mammoth, animal, extinct, prehistoric, woolly' },
      { emoji: '🦏', name: 'rhinoceros', keywords: 'rhinoceros, animal, africa, horn, endangered' },
      { emoji: '🦛', name: 'hippopotamus', keywords: 'hippopotamus, animal, africa, river, large' },
      { emoji: '🐭', name: 'mouse face', keywords: 'mouse, animal, rodent, small, cute' },
      { emoji: '🐁', name: 'mouse', keywords: 'mouse, animal, rodent, small, pet' },
      { emoji: '🐀', name: 'rat', keywords: 'rat, animal, rodent, pest, urban' },
      { emoji: '🐹', name: 'hamster', keywords: 'hamster, animal, rodent, pet, cute' },
      { emoji: '🐰', name: 'rabbit face', keywords: 'rabbit, animal, bunny, cute, pet' },
      { emoji: '🐇', name: 'rabbit', keywords: 'rabbit, animal, bunny, hop, pet' },
      { emoji: '🐿️', name: 'chipmunk', keywords: 'chipmunk, animal, rodent, squirrel, cute' },
      { emoji: '🦫', name: 'beaver', keywords: 'beaver, animal, rodent, dam, construction' }
    ]
  },
  {
    title: "Weather & Nature",
    icons: [
      { emoji: '☀️', name: 'sun', keywords: 'sun, weather, sunny, bright, day' },
      { emoji: '🌤️', name: 'sun behind small cloud', keywords: 'sun, cloud, weather, partly cloudy' },
      { emoji: '⛅', name: 'sun behind cloud', keywords: 'sun, cloud, weather, overcast' },
      { emoji: '🌥️', name: 'sun behind large cloud', keywords: 'sun, cloud, weather, mostly cloudy' },
      { emoji: '☁️', name: 'cloud', keywords: 'cloud, weather, cloudy, overcast' },
      { emoji: '🌦️', name: 'sun behind rain cloud', keywords: 'sun, rain, cloud, weather, shower' },
      { emoji: '🌧️', name: 'cloud with rain', keywords: 'rain, cloud, weather, precipitation' },
      { emoji: '⛈️', name: 'cloud with lightning and rain', keywords: 'lightning, rain, storm, weather, thunder' },
      { emoji: '🌩️', name: 'cloud with lightning', keywords: 'lightning, storm, weather, thunder' },
      { emoji: '🌨️', name: 'cloud with snow', keywords: 'snow, cloud, weather, winter, precipitation' }
    ]
  },
  {
    title: "Time & Calendar",
    icons: [
      { emoji: '🕐', name: 'one o\'clock', keywords: 'clock, time, one, hour, analog' },
      { emoji: '🕑', name: 'two o\'clock', keywords: 'clock, time, two, hour, analog' },
      { emoji: '🕒', name: 'three o\'clock', keywords: 'clock, time, three, hour, analog' },
      { emoji: '🕓', name: 'four o\'clock', keywords: 'clock, time, four, hour, analog' },
      { emoji: '🕔', name: 'five o\'clock', keywords: 'clock, time, five, hour, analog' },
      { emoji: '🕕', name: 'six o\'clock', keywords: 'clock, time, six, hour, analog' },
      { emoji: '🕖', name: 'seven o\'clock', keywords: 'clock, time, seven, hour, analog' },
      { emoji: '🕗', name: 'eight o\'clock', keywords: 'clock, time, eight, hour, analog' },
      { emoji: '🕘', name: 'nine o\'clock', keywords: 'clock, time, nine, hour, analog' },
      { emoji: '🕙', name: 'ten o\'clock', keywords: 'clock, time, ten, hour, analog' },
      { emoji: '🕚', name: 'eleven o\'clock', keywords: 'clock, time, eleven, hour, analog' },
      { emoji: '🕛', name: 'twelve o\'clock', keywords: 'clock, time, twelve, hour, analog' },
      { emoji: '🕜', name: 'one-thirty', keywords: 'clock, time, one thirty, half past, analog' },
      { emoji: '🕝', name: 'two-thirty', keywords: 'clock, time, two thirty, half past, analog' },
      { emoji: '🕞', name: 'three-thirty', keywords: 'clock, time, three thirty, half past, analog' },
      { emoji: '🕟', name: 'four-thirty', keywords: 'clock, time, four thirty, half past, analog' },
      { emoji: '🕠', name: 'five-thirty', keywords: 'clock, time, five thirty, half past, analog' },
      { emoji: '🕡', name: 'six-thirty', keywords: 'clock, time, six thirty, half past, analog' },
      { emoji: '🕢', name: 'seven-thirty', keywords: 'clock, time, seven thirty, half past, analog' },
      { emoji: '🕣', name: 'eight-thirty', keywords: 'clock, time, eight thirty, half past, analog' },
      { emoji: '🕤', name: 'nine-thirty', keywords: 'clock, time, nine thirty, half past, analog' },
      { emoji: '🕥', name: 'ten-thirty', keywords: 'clock, time, ten thirty, half past, analog' },
      { emoji: '🕦', name: 'eleven-thirty', keywords: 'clock, time, eleven thirty, half past, analog' },
      { emoji: '🕧', name: 'twelve-thirty', keywords: 'clock, time, twelve thirty, half past, analog' },
      { emoji: '⏰', name: 'alarm clock', keywords: 'alarm, clock, wake, time, morning' },
      { emoji: '⏱️', name: 'stopwatch', keywords: 'stopwatch, time, measure, sports, timing' },
      { emoji: '⏲️', name: 'timer clock', keywords: 'timer, clock, countdown, time, alarm' },
      { emoji: '🕰️', name: 'mantelpiece clock', keywords: 'clock, time, antique, vintage, decoration' },
      { emoji: '⌛', name: 'hourglass done', keywords: 'hourglass, time, sand, measure, done' },
      { emoji: '⏳', name: 'hourglass not done', keywords: 'hourglass, time, sand, measure, running' },
      { emoji: '📅', name: 'calendar', keywords: 'calendar, date, schedule, planning, time' },
      { emoji: '📆', name: 'tear-off calendar', keywords: 'calendar, date, tear-off, schedule, time' },
      { emoji: '🗓️', name: 'spiral calendar', keywords: 'calendar, spiral, date, schedule, time' }
    ]
  },
  {
    title: "Objects & Items",
    icons: [
      { emoji: '📱', name: 'mobile phone', keywords: 'phone, mobile, smartphone, communication, technology' },
      { emoji: '📲', name: 'mobile phone with arrow', keywords: 'phone, mobile, smartphone, communication, technology' },
      { emoji: '💻', name: 'laptop', keywords: 'laptop, computer, technology, work, portable' },
      { emoji: '⌨️', name: 'keyboard', keywords: 'keyboard, computer, input, typing, technology' },
      { emoji: '🖥️', name: 'desktop computer', keywords: 'desktop, computer, technology, work, monitor' },
      { emoji: '🖨️', name: 'printer', keywords: 'printer, print, document, office, technology' },
      { emoji: '🖱️', name: 'computer mouse', keywords: 'mouse, computer, cursor, click, technology' },
      { emoji: '🖲️', name: 'trackball', keywords: 'trackball, computer, input, cursor, technology' },
      { emoji: '💽', name: 'computer disk', keywords: 'disk, computer, storage, data, retro' },
      { emoji: '💾', name: 'floppy disk', keywords: 'floppy disk, save, storage, data, retro' },
      { emoji: '💿', name: 'optical disk', keywords: 'cd, disk, music, data, storage' },
      { emoji: '📀', name: 'dvd', keywords: 'dvd, disk, movie, video, storage' },
      { emoji: '🎥', name: 'movie camera', keywords: 'movie camera, film, recording, cinema, video' },
      { emoji: '🎬', name: 'clapper board', keywords: 'clapper board, movie, film, cinema, production' },
      { emoji: '📺', name: 'television', keywords: 'tv, television, entertainment, screen, technology' },
      { emoji: '📻', name: 'radio', keywords: 'radio, music, broadcast, entertainment, audio' },
      { emoji: '📷', name: 'camera', keywords: 'camera, photo, photography, capture, technology' },
      { emoji: '📸', name: 'camera with flash', keywords: 'camera, photo, photography, flash, capture' },
      { emoji: '📹', name: 'video camera', keywords: 'video camera, recording, film, capture, technology' },
      { emoji: '📼', name: 'videocassette', keywords: 'videocassette, tape, video, recording, retro' },
      { emoji: '🔍', name: 'magnifying glass tilted left', keywords: 'search, magnify, zoom, find, tool' },
      { emoji: '🔎', name: 'magnifying glass tilted right', keywords: 'search, magnify, zoom, find, tool' },
      { emoji: '🕯️', name: 'candle', keywords: 'candle, light, flame, decoration, romantic' },
      { emoji: '💡', name: 'light bulb', keywords: 'light bulb, idea, innovation, bright, technology' },
      { emoji: '🔦', name: 'flashlight', keywords: 'flashlight, torch, light, beam, tool' },
      { emoji: '🏮', name: 'red paper lantern', keywords: 'lantern, light, decoration, asian, festival' },
      { emoji: '🪔', name: 'diya lamp', keywords: 'diya, lamp, light, indian, celebration' },
      { emoji: '📞', name: 'telephone receiver', keywords: 'phone, telephone, call, communication, contact' },
      { emoji: '📟', name: 'pager', keywords: 'pager, beeper, communication, retro, business' },
      { emoji: '📠', name: 'fax machine', keywords: 'fax, machine, document, communication, office' },
      { emoji: '🎙️', name: 'studio microphone', keywords: 'microphone, studio, recording, audio, music' },
      { emoji: '🎚️', name: 'level slider', keywords: 'slider, control, audio, music, adjust' },
      { emoji: '🎛️', name: 'control knobs', keywords: 'knobs, control, audio, music, adjust' },
      { emoji: '🧭', name: 'compass', keywords: 'compass, navigation, direction, travel, orienteering' },
      { emoji: '⏱️', name: 'stopwatch', keywords: 'stopwatch, time, measure, sports, timing' },
      { emoji: '⏲️', name: 'timer clock', keywords: 'timer, clock, countdown, time, alarm' },
      { emoji: '⏰', name: 'alarm clock', keywords: 'alarm, clock, wake, time, morning' },
      { emoji: '🕰️', name: 'mantelpiece clock', keywords: 'clock, time, antique, vintage, decoration' },
      { emoji: '⌛', name: 'hourglass done', keywords: 'hourglass, time, sand, measure, done' },
      { emoji: '⏳', name: 'hourglass not done', keywords: 'hourglass, time, sand, measure, running' },
      { emoji: '📡', name: 'satellite antenna', keywords: 'satellite, antenna, signal, communication, technology' },
      { emoji: '🔋', name: 'battery', keywords: 'battery, power, energy, charge, technology' },
      { emoji: '🔌', name: 'electric plug', keywords: 'plug, electricity, power, charge, technology' }
    ]
  },
  {
    title: "Symbols & Shapes",
    icons: [
      { emoji: '⭐', name: 'star', keywords: 'star, rating, favorite, shine, bright' },
      { emoji: '🌟', name: 'glowing star', keywords: 'star, glowing, shine, bright, sparkle' },
      { emoji: '✨', name: 'sparkles', keywords: 'sparkles, shine, glitter, magic, beautiful' },
      { emoji: '💫', name: 'dizzy', keywords: 'dizzy, star, sparkle, confusion, woah' },
      { emoji: '⚡', name: 'high voltage', keywords: 'lightning, electricity, power, energy, bolt' },
      { emoji: '🔥', name: 'fire', keywords: 'fire, flame, hot, burn, heat' },
      { emoji: '💧', name: 'droplet', keywords: 'droplet, water, liquid, rain, tear' },
      { emoji: '🌊', name: 'wave', keywords: 'wave, ocean, sea, water, beach' },
      { emoji: '🌈', name: 'rainbow', keywords: 'rainbow, colorful, sky, weather, beautiful' },
      { emoji: '🌙', name: 'crescent moon', keywords: 'moon, crescent, night, sky, lunar' },
      { emoji: '💎', name: 'gem stone', keywords: 'gem, diamond, precious, jewel, sparkle' },
      { emoji: '🔶', name: 'large orange diamond', keywords: 'diamond, orange, shape, geometric' },
      { emoji: '🔷', name: 'large blue diamond', keywords: 'diamond, blue, shape, geometric' },
      { emoji: '🔸', name: 'small orange diamond', keywords: 'diamond, orange, shape, small' },
      { emoji: '🔹', name: 'small blue diamond', keywords: 'diamond, blue, shape, small' },
      { emoji: '🔺', name: 'red triangle pointed up', keywords: 'triangle, red, up, shape, geometric' },
      { emoji: '🔻', name: 'red triangle pointed down', keywords: 'triangle, red, down, shape, geometric' },
      { emoji: '💠', name: 'diamond with a dot', keywords: 'diamond, dot, shape, geometric, center' },
      { emoji: '🔘', name: 'radio button', keywords: 'radio button, select, choice, option, circle' },
      { emoji: '🔴', name: 'red circle', keywords: 'circle, red, shape, geometric' },
      { emoji: '🟠', name: 'orange circle', keywords: 'circle, orange, shape, geometric' },
      { emoji: '🟡', name: 'yellow circle', keywords: 'circle, yellow, shape, geometric' },
      { emoji: '🟢', name: 'green circle', keywords: 'circle, green, shape, geometric' },
      { emoji: '🔵', name: 'blue circle', keywords: 'circle, blue, shape, geometric' },
      { emoji: '🟣', name: 'purple circle', keywords: 'circle, purple, shape, geometric' },
      { emoji: '⚫', name: 'black circle', keywords: 'circle, black, shape, geometric' },
      { emoji: '⚪', name: 'white circle', keywords: 'circle, white, shape, geometric' },
      { emoji: '🟤', name: 'brown circle', keywords: 'circle, brown, shape, geometric' }
    ]
  },
  {
    title: "Food & Drinks Extended",
    icons: [
      { emoji: '🍎', name: 'red apple', keywords: 'apple, red, fruit, healthy, food' },
      { emoji: '🍐', name: 'pear', keywords: 'pear, fruit, green, food, healthy' },
      { emoji: '🍊', name: 'orange', keywords: 'orange, fruit, citrus, food, vitamin c' },
      { emoji: '🍋', name: 'lemon', keywords: 'lemon, fruit, citrus, sour, food' },
      { emoji: '🍌', name: 'banana', keywords: 'banana, fruit, yellow, food, potassium' },
      { emoji: '🍉', name: 'watermelon', keywords: 'watermelon, fruit, summer, food, refreshing' },
      { emoji: '🍇', name: 'grapes', keywords: 'grapes, fruit, purple, food, wine' },
      { emoji: '🍓', name: 'strawberry', keywords: 'strawberry, fruit, red, food, sweet' },
      { emoji: '🫐', name: 'blueberries', keywords: 'blueberries, fruit, blue, food, antioxidant' },
      { emoji: '🍈', name: 'melon', keywords: 'melon, fruit, food, summer, sweet' },
      { emoji: '🍒', name: 'cherries', keywords: 'cherries, fruit, red, food, sweet' },
      { emoji: '🍑', name: 'peach', keywords: 'peach, fruit, orange, food, sweet' },
      { emoji: '🥭', name: 'mango', keywords: 'mango, fruit, orange, food, tropical' },
      { emoji: '🍍', name: 'pineapple', keywords: 'pineapple, fruit, yellow, food, tropical' },
      { emoji: '🥥', name: 'coconut', keywords: 'coconut, fruit, brown, food, tropical' },
      { emoji: '🥝', name: 'kiwi fruit', keywords: 'kiwi, fruit, green, food, vitamin c' },
      { emoji: '🍅', name: 'tomato', keywords: 'tomato, vegetable, red, food, healthy' },
      { emoji: '🥑', name: 'avocado', keywords: 'avocado, vegetable, green, food, healthy' },
      { emoji: '🥦', name: 'broccoli', keywords: 'broccoli, vegetable, green, food, healthy' },
      { emoji: '🥬', name: 'leafy green', keywords: 'leafy green, vegetable, green, food, healthy' },
      { emoji: '🥒', name: 'cucumber', keywords: 'cucumber, vegetable, green, food, healthy' },
      { emoji: '🌶️', name: 'hot pepper', keywords: 'hot pepper, vegetable, red, food, spicy' },
      { emoji: '🫑', name: 'bell pepper', keywords: 'bell pepper, vegetable, green, food, healthy' },
      { emoji: '🌽', name: 'ear of corn', keywords: 'corn, vegetable, yellow, food, grain' },
      { emoji: '🥕', name: 'carrot', keywords: 'carrot, vegetable, orange, food, healthy' },
      { emoji: '🫒', name: 'olive', keywords: 'olive, vegetable, green, food, mediterranean' },
      { emoji: '🧄', name: 'garlic', keywords: 'garlic, vegetable, white, food, seasoning' },
      { emoji: '🧅', name: 'onion', keywords: 'onion, vegetable, white, food, cooking' },
      { emoji: '🥔', name: 'potato', keywords: 'potato, vegetable, brown, food, starch' },
      { emoji: '🍠', name: 'roasted sweet potato', keywords: 'sweet potato, vegetable, orange, food, healthy' },
      { emoji: '🥐', name: 'croissant', keywords: 'croissant, bread, pastry, food, french' },
      { emoji: '🥯', name: 'bagel', keywords: 'bagel, bread, food, breakfast, round' },
      { emoji: '🍞', name: 'bread', keywords: 'bread, food, staple, wheat, bakery' },
      { emoji: '🥖', name: 'baguette bread', keywords: 'baguette, bread, french, food, long' },
      { emoji: '🥨', name: 'pretzel', keywords: 'pretzel, bread, food, twisted, salty' },
      { emoji: '🧀', name: 'cheese wedge', keywords: 'cheese, dairy, food, yellow, protein' },
      { emoji: '🥚', name: 'egg', keywords: 'egg, food, protein, breakfast, white' },
      { emoji: '🍳', name: 'cooking', keywords: 'cooking, pan, food, breakfast, fry' },
      { emoji: '🧈', name: 'butter', keywords: 'butter, dairy, food, yellow, spread' },
      { emoji: '🥞', name: 'pancakes', keywords: 'pancakes, food, breakfast, sweet, stack' },
      { emoji: '🧇', name: 'waffle', keywords: 'waffle, food, breakfast, sweet, grid' },
      { emoji: '🥓', name: 'bacon', keywords: 'bacon, meat, food, breakfast, salty' },
      { emoji: '🥩', name: 'cut of meat', keywords: 'meat, food, protein, steak, red' },
      { emoji: '🍗', name: 'poultry leg', keywords: 'chicken, meat, food, protein, leg' },
      { emoji: '🍖', name: 'meat on bone', keywords: 'meat, food, protein, bone, red' },
      { emoji: '🦴', name: 'bone', keywords: 'bone, skeleton, anatomy, structure' },
      { emoji: '🌭', name: 'hot dog', keywords: 'hot dog, food, sausage, bun, fast food' },
      { emoji: '🍔', name: 'hamburger', keywords: 'hamburger, food, burger, bun, fast food' }
    ]
  },
  {
    title: "Activities & Hobbies",
    icons: [
      { emoji: '🎨', name: 'artist palette', keywords: 'art, painting, creativity, artist, palette' },
      { emoji: '🎭', name: 'performing arts', keywords: 'theater, drama, performance, entertainment, arts' },
      { emoji: '🎪', name: 'circus tent', keywords: 'circus, entertainment, show, carnival, performance' },
      { emoji: '🎯', name: 'direct hit', keywords: 'target, aim, precision, game, darts' },
      { emoji: '🎲', name: 'game die', keywords: 'dice, game, gambling, chance, luck' },
      { emoji: '🎮', name: 'video game', keywords: 'video game, gaming, entertainment, console, play' },
      { emoji: '🎸', name: 'guitar', keywords: 'guitar, music, instrument, rock, string' },
      { emoji: '🎹', name: 'musical keyboard', keywords: 'piano, keyboard, music, instrument, keys' },
      { emoji: '🎺', name: 'trumpet', keywords: 'trumpet, music, instrument, brass, horn' },
      { emoji: '🎻', name: 'violin', keywords: 'violin, music, instrument, string, classical' },
      { emoji: '🥁', name: 'drum', keywords: 'drum, music, instrument, percussion, beat' },
      { emoji: '🎤', name: 'microphone', keywords: 'microphone, music, singing, performance, audio' },
      { emoji: '🎧', name: 'headphone', keywords: 'headphone, music, audio, listening, sound' },
      { emoji: '🎼', name: 'musical score', keywords: 'music score, sheet music, composition, notes' },
      { emoji: '🎵', name: 'musical note', keywords: 'musical note, music, sound, melody, tune' },
      { emoji: '🎶', name: 'musical notes', keywords: 'musical notes, music, melody, harmony, tune' }
    ]
  },
  {
    title: "Travel & Places",
    icons: [
      { emoji: '🗺️', name: 'world map', keywords: 'map, world, geography, travel, location' },
      { emoji: '🗽', name: 'statue of liberty', keywords: 'statue of liberty, new york, freedom, landmark' },
      { emoji: '🗼', name: 'tokyo tower', keywords: 'tokyo tower, japan, landmark, building' },
      { emoji: '🏰', name: 'castle', keywords: 'castle, building, historical, architecture, medieval' },
      { emoji: '🏯', name: 'japanese castle', keywords: 'japanese castle, building, historical, architecture' },
      { emoji: '🏛️', name: 'classical building', keywords: 'classical building, architecture, historical, greek' },
      { emoji: '⛪', name: 'church', keywords: 'church, religion, christian, building, worship' },
      { emoji: '🕌', name: 'mosque', keywords: 'mosque, religion, islam, building, worship' },
      { emoji: '🛕', name: 'hindu temple', keywords: 'hindu temple, religion, building, worship' },
      { emoji: '🕍', name: 'synagogue', keywords: 'synagogue, religion, judaism, building, worship' },
      { emoji: '⛩️', name: 'shinto shrine', keywords: 'shinto shrine, religion, japan, building, worship' },
      { emoji: '🕋', name: 'kaaba', keywords: 'kaaba, mecca, islam, religion, building' },
      { emoji: '⛲', name: 'fountain', keywords: 'fountain, water, decoration, park, public' },
      { emoji: '⛺', name: 'tent', keywords: 'tent, camping, outdoor, shelter, temporary' },
      { emoji: '🌁', name: 'foggy', keywords: 'foggy, weather, mist, cloudy, atmosphere' },
      { emoji: '🌃', name: 'night with stars', keywords: 'night, stars, sky, evening, dark' },
      { emoji: '🏙️', name: 'cityscape', keywords: 'cityscape, urban, buildings, skyline, city' },
      { emoji: '🌄', name: 'sunrise over mountains', keywords: 'sunrise, mountains, morning, nature, landscape' },
      { emoji: '🌅', name: 'sunrise', keywords: 'sunrise, morning, dawn, sky, horizon' },
      { emoji: '🌆', name: 'cityscape at dusk', keywords: 'cityscape, dusk, evening, urban, sunset' },
      { emoji: '🌇', name: 'sunset', keywords: 'sunset, evening, dusk, sky, horizon' },
      { emoji: '🌉', name: 'bridge at night', keywords: 'bridge, night, city, urban, architecture' },
      { emoji: '♨️', name: 'hot springs', keywords: 'hot springs, spa, relaxation, japan, thermal' },
      { emoji: '🎠', name: 'carousel horse', keywords: 'carousel, amusement park, ride, horse, fun' },
      { emoji: '🎡', name: 'ferris wheel', keywords: 'ferris wheel, amusement park, ride, carnival, fun' },
      { emoji: '🎢', name: 'roller coaster', keywords: 'roller coaster, amusement park, ride, thrill, fun' },
      { emoji: '💈', name: 'barber pole', keywords: 'barber pole, haircut, salon, traditional, sign' },
      { emoji: '🎪', name: 'circus tent', keywords: 'circus tent, entertainment, show, carnival, performance' },
      { emoji: '🚂', name: 'locomotive', keywords: 'train, locomotive, transportation, travel, steam' },
      { emoji: '🚃', name: 'railway car', keywords: 'train car, railway, transportation, travel, commute' },
      { emoji: '🚄', name: 'high-speed train', keywords: 'bullet train, high speed, transportation, travel, fast' },
      { emoji: '🚅', name: 'high-speed train with bullet nose', keywords: 'bullet train, high speed, transportation, travel, fast' },
      { emoji: '🚆', name: 'train', keywords: 'train, railway, transportation, travel, commute' },
      { emoji: '🚇', name: 'metro', keywords: 'subway, metro, underground, transportation, travel' },
      { emoji: '🚈', name: 'light rail', keywords: 'light rail, train, transportation, travel, urban' },
      { emoji: '🚉', name: 'station', keywords: 'train station, transportation, travel, platform, commute' },
      { emoji: '🚊', name: 'tram', keywords: 'tram, streetcar, transportation, travel, public transport' },
      { emoji: '🚝', name: 'monorail', keywords: 'monorail, train, transportation, travel, modern' },
      { emoji: '🚞', name: 'mountain railway', keywords: 'mountain railway, train, transportation, travel' },
      { emoji: '🚋', name: 'tram car', keywords: 'tram, streetcar, transportation, travel, public transport' },
      { emoji: '🚌', name: 'bus', keywords: 'bus, public transport, transportation, travel, commute' },
      { emoji: '🚍', name: 'oncoming bus', keywords: 'bus, public transport, transportation, travel' },
      { emoji: '🚎', name: 'trolleybus', keywords: 'trolleybus, bus, public transport, transportation' },
      { emoji: '🚐', name: 'minibus', keywords: 'minibus, van, transportation, travel, vehicle' },
      { emoji: '🚑', name: 'ambulance', keywords: 'ambulance, emergency, medical, health, vehicle' },
      { emoji: '🚒', name: 'fire engine', keywords: 'fire engine, fire truck, emergency, rescue, vehicle' },
      { emoji: '🚓', name: 'police car', keywords: 'police car, law enforcement, emergency, vehicle' },
      { emoji: '🚔', name: 'oncoming police car', keywords: 'police car, law enforcement, emergency, vehicle' }
    ]
  },
  {
    title: "Communication",
    icons: [
      { emoji: '📞', name: 'telephone receiver', keywords: 'phone, telephone, call, communication, contact' },
      { emoji: '📟', name: 'pager', keywords: 'pager, beeper, communication, retro, business' },
      { emoji: '📠', name: 'fax machine', keywords: 'fax, machine, document, communication, office' },
      { emoji: '📺', name: 'television', keywords: 'tv, television, entertainment, screen, technology' },
      { emoji: '📻', name: 'radio', keywords: 'radio, music, broadcast, entertainment, audio' },
      { emoji: '🎙️', name: 'studio microphone', keywords: 'microphone, studio, recording, audio, music' },
      { emoji: '🎚️', name: 'level slider', keywords: 'slider, control, audio, music, adjust' },
      { emoji: '🎛️', name: 'control knobs', keywords: 'knobs, control, audio, music, adjust' },
      { emoji: '🎤', name: 'microphone', keywords: 'microphone, music, singing, performance, audio' },
      { emoji: '🎧', name: 'headphone', keywords: 'headphone, music, audio, listening, sound' },
      { emoji: '🎼', name: 'musical score', keywords: 'music score, sheet music, composition, notes' },
      { emoji: '🎵', name: 'musical note', keywords: 'musical note, music, sound, melody, tune' },
      { emoji: '📧', name: 'e-mail', keywords: 'email, mail, communication, message, digital' },
      { emoji: '📨', name: 'incoming envelope', keywords: 'envelope, mail, letter, communication, incoming' },
      { emoji: '📩', name: 'envelope with arrow', keywords: 'envelope, mail, arrow, communication, outgoing' },
      { emoji: '📪', name: 'closed mailbox with lowered flag', keywords: 'mailbox, mail, post, communication, closed' },
      { emoji: '📫', name: 'closed mailbox with raised flag', keywords: 'mailbox, mail, post, communication, flag' },
      { emoji: '📬', name: 'open mailbox with raised flag', keywords: 'mailbox, mail, post, communication, open' },
      { emoji: '📭', name: 'open mailbox with lowered flag', keywords: 'mailbox, mail, post, communication, empty' },
      { emoji: '📮', name: 'postbox', keywords: 'postbox, mail, post, communication, public' },
      { emoji: '🗳️', name: 'ballot box with ballot', keywords: 'ballot box, voting, election, democracy, ballot' }
    ]
  },
  {
    title: "Miscellaneous & Fun",
    icons: [
      { emoji: '🎈', name: 'balloon', keywords: 'balloon, party, celebration, birthday, fun' },
      { emoji: '🎉', name: 'party popper', keywords: 'party popper, celebration, confetti, fun, party' },
      { emoji: '🎊', name: 'confetti ball', keywords: 'confetti ball, celebration, party, fun, festive' },
      { emoji: '🎋', name: 'tanabata tree', keywords: 'tanabata tree, japan, festival, wish, bamboo' },
      { emoji: '🎍', name: 'pine decoration', keywords: 'pine decoration, japan, new year, traditional' },
      { emoji: '🎎', name: 'japanese dolls', keywords: 'japanese dolls, japan, traditional, festival' },
      { emoji: '🎏', name: 'carp streamer', keywords: 'carp streamer, japan, children day, fish' },
      { emoji: '🎐', name: 'wind chime', keywords: 'wind chime, japan, summer, sound, decoration' },
      { emoji: '🎀', name: 'ribbon', keywords: 'ribbon, decoration, gift, bow, pretty' },
      { emoji: '🎁', name: 'wrapped gift', keywords: 'wrapped gift, present, birthday, celebration, surprise' },
      { emoji: '🎗️', name: 'reminder ribbon', keywords: 'reminder ribbon, awareness, cause, support' },
      { emoji: '🎟️', name: 'admission tickets', keywords: 'admission tickets, event, concert, show, entry' },
      { emoji: '🎫', name: 'ticket', keywords: 'ticket, admission, event, show, entry' },
      { emoji: '🎖️', name: 'military medal', keywords: 'military medal, award, honor, service, recognition' },
      { emoji: '🏆', name: 'trophy', keywords: 'trophy, award, winner, achievement, prize' },
      { emoji: '🏅', name: 'sports medal', keywords: 'sports medal, award, olympics, achievement, winner' },
      { emoji: '🥇', name: '1st place medal', keywords: '1st place medal, gold, winner, olympics, achievement' },
      { emoji: '🥈', name: '2nd place medal', keywords: '2nd place medal, silver, runner up, olympics' },
      { emoji: '🥉', name: '3rd place medal', keywords: '3rd place medal, bronze, third, olympics' }
    ]
  }
];

// Sortable Item Component
const SortableItem: React.FC<{ category: Category; onEdit: (category: Category) => void; onDelete: (category: Category) => void; onResetServiceIcons: (category: Category) => void }> = ({ category, onEdit, onDelete, onResetServiceIcons }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false);
  const actionsDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(event.target as Node)) {
        setIsActionsDropdownOpen(false);
      }
    };

    if (isActionsDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isActionsDropdownOpen]);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    // dnd-kit applies transforms to each row which creates stacking contexts.
    // Force the active row above its siblings so the dropdown can't render "under" the next row.
    zIndex: isActionsDropdownOpen ? 1000 : undefined,
  };

  const categoryColorHex = category.background_color?.trim();
  const categoryRgb = categoryColorHex ? hexToRgb(categoryColorHex) : null;
  const categoryGradient = (!isDragging && categoryRgb)
    ? `linear-gradient(90deg, rgba(${categoryRgb.r}, ${categoryRgb.g}, ${categoryRgb.b}, 0.65) 0%, rgba(${categoryRgb.r}, ${categoryRgb.g}, ${categoryRgb.b}, 0.25) 35%, rgba(255, 255, 255, 0) 75%)`
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        backgroundColor: categoryGradient ? '#ffffff' : undefined,
        backgroundImage: categoryGradient
      }}
      className={`flex items-center justify-between p-4 border border-gray-200 rounded-lg transition-colors relative overflow-visible ${
        isDragging ? 'bg-blue-50 border-blue-300 shadow-lg' : 'hover:bg-gray-50'
      } ${isActionsDropdownOpen ? 'z-50' : ''}`}
    >
      <div className="flex items-center space-x-3">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600"
        >
          <GripVertical className="w-4 h-4" />
        </div>
        <span className="text-2xl">{category.icon}</span>
        <div>
          <h3 className="font-medium text-gray-900">{category.name}</h3>
          {category.description && (
            <p className="text-sm text-gray-500">{category.description}</p>
          )}
        </div>
      </div>
      {/* Actions Dropdown */}
      <div className="relative z-50" ref={actionsDropdownRef}>
        <button
          onClick={() => setIsActionsDropdownOpen(!isActionsDropdownOpen)}
          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition-colors"
          title="Actions"
        >
          <Settings className="w-4 h-4" />
        </button>
        
        {isActionsDropdownOpen && (
          <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-xl z-[99999]">
            <div className="py-1">
              <button
                onClick={() => {
                  onEdit(category);
                  setIsActionsDropdownOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={() => {
                  onResetServiceIcons(category);
                  setIsActionsDropdownOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-sm text-orange-600 hover:text-orange-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Reset Service Icons
              </button>
              <button
                onClick={() => {
                  onDelete(category);
                  setIsActionsDropdownOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ManageCategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const { currentBusiness } = useBusiness();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isIconDropdownOpen, setIsIconDropdownOpen] = useState(false);
  const [iconSearchTerm, setIconSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '🛠️',
    display_order: 0,
    background_color: CATEGORY_BACKGROUND_COLORS[0].value
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  const [showResetIconsModal, setShowResetIconsModal] = useState(false);
  const [categoryToResetIcons, setCategoryToResetIcons] = useState<Category | null>(null);
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });
  const navigate = useNavigate();
  const location = useLocation();

  // Refs
  const categoryNameInputRef = useRef<HTMLInputElement>(null);

  // Sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const handleSectionChange = (section: string) => {
    navigate(`/dashboard/business?section=${section}`);
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Get back navigation info based on where user came from
  const getBackNavigationInfo = () => {
    const urlParams = new URLSearchParams(location.search);
    const from = urlParams.get('from');
    const baseParams = new URLSearchParams(location.search);
    baseParams.delete('from');

    const dashboardParams = new URLSearchParams(baseParams);
    dashboardParams.delete('serviceId');
    dashboardParams.set('section', 'services');
    const servicesDashboardPath = `/dashboard/business?${dashboardParams.toString()}`;
    
    switch (from) {
      case 'add-service':
        return {
          text: 'Back to Add Service',
          path: `/services/add?${(() => {
            const params = new URLSearchParams(baseParams);
            params.delete('serviceId');
            params.set('fromCategories', 'true');
            return params.toString();
          })()}`
        };
      case 'edit-service':
        const serviceId = urlParams.get('serviceId');
        if (serviceId) {
          return {
            text: 'Back to Edit Service',
            path: `/services/edit/${serviceId}?${(() => {
              const params = new URLSearchParams(baseParams);
              params.delete('serviceId');
              params.set('fromCategories', 'true');
              return params.toString();
            })()}`
          };
        }
        return { text: 'Back to Services', path: servicesDashboardPath };
      case 'duplicate-service':
        const duplicateServiceId = urlParams.get('serviceId');
        if (duplicateServiceId) {
          return {
            text: 'Back to Duplicate Service',
            path: `/services/duplicate/${duplicateServiceId}?${(() => {
              const params = new URLSearchParams(baseParams);
              params.delete('serviceId');
              params.set('fromCategories', 'true');
              return params.toString();
            })()}`
          };
        }
        return { text: 'Back to Services', path: servicesDashboardPath };
      case 'services':
      default:
        return { text: 'Back to Services', path: servicesDashboardPath };
    }
  };

  // Handle back navigation based on where user came from
  const handleBackNavigation = () => {
    const { path } = getBackNavigationInfo();
    navigate(path);
  };

  useEffect(() => {
    if (currentBusiness?.id) {
      loadCategories();
    }
  }, [currentBusiness?.id]);

  // Close icon dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.icon-dropdown-container')) {
        setIsIconDropdownOpen(false);
        setIconSearchTerm(''); // Clear search when dropdown closes
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      if (!currentBusiness?.id) {
        setCategories([]);
        return;
      }
      const categoriesData = await apiService.getServiceCategories(currentBusiness.id);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required';
    }

    if (formData.name.length > 50) {
      newErrors.name = 'Category name must be 50 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      if (editingCategory) {
        await apiService.updateServiceCategory(editingCategory.id, formData);
        setToast({ message: 'Category updated successfully!', type: 'success' });
      } else {
        await apiService.createServiceCategory({ ...formData, businessId: currentBusiness?.id });
        setToast({ message: 'Category created successfully!', type: 'success' });
      }

      // Show success message
      setShowSuccessMessage(true);
      setErrors({});

      // Reset form
      setFormData({ name: '', description: '', icon: '🛠️', display_order: 0, background_color: CATEGORY_BACKGROUND_COLORS[0].value });
      setIconSearchTerm(''); // Clear search when resetting form
      
      // Reset to initial state - no form shown
      setEditingCategory(null);
      setIsAdding(false);

      // Reload categories
      await loadCategories();

      // Hide success message after 3 seconds
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000);

      // Hide toast after 4 seconds
      setTimeout(() => {
        setToast(null);
      }, 4000);
    } catch (error: any) {
      console.error('Error saving category:', error);
      setToast({ message: 'Failed to save category', type: 'error' });
      if (error.response?.data?.error) {
        setErrors({ general: error.response.data.error });
      } else {
        setErrors({ general: 'Failed to save category' });
      }
      
      // Hide error toast after 4 seconds
      setTimeout(() => {
        setToast(null);
      }, 4000);
    }
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      icon: category.icon || '🛠️',
      display_order: category.display_order || 0,
      background_color: category.background_color || CATEGORY_BACKGROUND_COLORS[0].value
    });
    setIsAdding(false);
    setErrors({});
    setShowSuccessMessage(false);
    setIconSearchTerm(''); // Clear search when editing
  };

  const handleDeleteClick = (category: Category) => {
    setCategoryToDelete(category);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;

    try {
      await apiService.deleteServiceCategory(categoryToDelete.id, currentBusiness?.id);
      setToast({ message: 'Category deleted successfully!', type: 'success' });
      await loadCategories();
      
      // Hide toast after 4 seconds
      setTimeout(() => {
        setToast(null);
      }, 4000);
    } catch (error: any) {
      console.error('Error deleting category:', error);
      setToast({ message: 'Failed to delete category', type: 'error' });
      if (error.response?.data?.error) {
        setAlertModal({
          isOpen: true,
          title: 'Error',
          message: error.response.data.error,
          type: 'error'
        });
      } else {
        setAlertModal({
          isOpen: true,
          title: 'Error',
          message: 'Failed to delete category',
          type: 'error'
        });
      }
      
      // Hide error toast after 4 seconds
      setTimeout(() => {
        setToast(null);
      }, 4000);
    } finally {
      setShowDeleteModal(false);
      setCategoryToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setCategoryToDelete(null);
  };

  const handleResetServiceIconsClick = (category: Category) => {
    setCategoryToResetIcons(category);
    setShowResetIconsModal(true);
  };

  const handleResetServiceIconsCancel = () => {
    setShowResetIconsModal(false);
    setCategoryToResetIcons(null);
  };

  const handleResetServiceIconsConfirm = async () => {
    if (!categoryToResetIcons || !currentBusiness?.id) {
      setShowResetIconsModal(false);
      setCategoryToResetIcons(null);
      return;
    }

    try {
      // Get all services for the business
      const services = await apiService.getBusinessServices(currentBusiness.id);
      
      // Filter services in this category
      const categoryServices = services.filter((service: any) => 
        service.categoryId === categoryToResetIcons.id || service.category?.id === categoryToResetIcons.id
      );

      if (categoryServices.length === 0) {
        setToast({ message: `No services found in "${categoryToResetIcons.name}" category`, type: 'error' });
        setTimeout(() => setToast(null), 4000);
        setShowResetIconsModal(false);
        setCategoryToResetIcons(null);
        return;
      }

      // Reset icon for each service (set to undefined/empty to use category icon)
      let successCount = 0;
      let errorCount = 0;

      for (const service of categoryServices) {
        try {
          // Send null to clear the icon (undefined gets omitted from JSON)
          await apiService.updateService(service.id, { icon: null });
          successCount++;
        } catch (error) {
          console.error(`Error resetting icon for service ${service.id}:`, error);
          errorCount++;
        }
      }

      if (errorCount === 0) {
        setToast({ 
          message: `Successfully reset icons for ${successCount} service${successCount !== 1 ? 's' : ''} in "${categoryToResetIcons.name}"`, 
          type: 'success' 
        });
      } else {
        setToast({ 
          message: `Reset icons for ${successCount} service${successCount !== 1 ? 's' : ''}, but ${errorCount} failed`, 
          type: 'error' 
        });
      }

      setTimeout(() => setToast(null), 4000);
    } catch (error: any) {
      console.error('Error resetting service icons:', error);
      setToast({ message: 'Failed to reset service icons', type: 'error' });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setShowResetIconsModal(false);
      setCategoryToResetIcons(null);
    }
  };

  const closeAlertModal = () => {
    setAlertModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleCancel = () => {
    setFormData({ name: '', description: '', icon: '🛠️', display_order: 0, background_color: CATEGORY_BACKGROUND_COLORS[0].value });
    setEditingCategory(null);
    setIsAdding(false);
    setErrors({});
    setShowSuccessMessage(false);
    setIconSearchTerm(''); // Clear search when canceling
  };

  const handleAddNew = () => {
    setIsAdding(true);
    setEditingCategory(null);
    
    // Calculate the next available display order
    const nextDisplayOrder = categories.length + 1;
    
    setFormData({ name: '', description: '', icon: '🛠️', display_order: nextDisplayOrder, background_color: CATEGORY_BACKGROUND_COLORS[0].value });
    setErrors({});
    setShowSuccessMessage(false);
    setIconSearchTerm(''); // Clear search when adding new
    
    // Focus on the category name input after a short delay to ensure the form is rendered
    setTimeout(() => {
      categoryNameInputRef.current?.focus();
    }, 100);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = categories.findIndex(cat => cat.id === active.id);
      const newIndex = categories.findIndex(cat => cat.id === over?.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        // Create reordered array
        const reorderedCategories = arrayMove(categories, oldIndex, newIndex);

        // Update display_order for all categories
        const updatedCategories = reorderedCategories.map((category, index) => ({
          ...category,
          display_order: index + 1
        }));

        // Update local state immediately for better UX
        setCategories(updatedCategories);

        try {
          // Update all categories with new display_order values
          const updatePromises = updatedCategories.map(category =>
            apiService.updateServiceCategory(category.id, {
              name: category.name,
              description: category.description,
              icon: category.icon,
              display_order: category.display_order,
              background_color: category.background_color
            })
          );

          await Promise.all(updatePromises);
          setToast({ message: 'Category order updated successfully!', type: 'success' });
          
          // Hide toast after 3 seconds
          setTimeout(() => {
            setToast(null);
          }, 3000);
        } catch (error) {
          console.error('Error updating category order:', error);
          setToast({ message: 'Failed to update category order', type: 'error' });
          
          // Revert to original order on error
          await loadCategories();
          
          // Hide error toast after 4 seconds
          setTimeout(() => {
            setToast(null);
          }, 4000);
        }
      }
    }
  };

  const handleIconSelect = (icon: string) => {
    setFormData({ ...formData, icon });
    setIsIconDropdownOpen(false);
    setIconSearchTerm(''); // Clear search when icon is selected
  };

  // Filter icons based on search term
  const getFilteredIconGroups = () => {
    if (!iconSearchTerm.trim()) {
      return GROUPED_EMOJI_ICONS;
    }

    const searchLower = iconSearchTerm.toLowerCase();
    return GROUPED_EMOJI_ICONS
      .map(group => ({
        ...group,
        icons: group.icons.filter(icon => 
          group.title.toLowerCase().includes(searchLower) ||
          icon.name.toLowerCase().includes(searchLower) ||
          icon.keywords.toLowerCase().includes(searchLower)
        )
      }))
      .filter(group => group.icons.length > 0);
  };

  const renderIconDropdown = () => (
    <div className="relative icon-dropdown-container">
      <button
        type="button"
        onClick={() => {
          setIsIconDropdownOpen(!isIconDropdownOpen);
          if (!isIconDropdownOpen) {
            setIconSearchTerm(''); // Clear search when opening dropdown
          }
        }}
        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-left"
      >
        <div className="flex items-center space-x-2">
          <span className="text-xl">{formData.icon}</span>
          <span className="text-gray-700">Select Icon</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isIconDropdownOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isIconDropdownOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-96 overflow-y-auto">
          {/* Search Input */}
          <div className="sticky top-0 bg-white border-b border-gray-200 p-3 z-10 shadow-sm">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={iconSearchTerm}
                onChange={(e) => setIconSearchTerm(e.target.value)}
                placeholder="Search icons or categories..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-gray-50"
                autoFocus
              />
            </div>
          </div>
          
          <div className="p-3">
            {getFilteredIconGroups().length > 0 ? (
              getFilteredIconGroups().map((group, groupIndex) => (
                <div key={groupIndex} className="mb-4 last:mb-0">
                  <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 px-1">
                    {group.title}
                  </h4>
                                     <div className="grid grid-cols-8 gap-2 mb-3">
                     {group.icons.map((icon, iconIndex) => (
                       <button
                         key={`${groupIndex}-${iconIndex}`}
                         type="button"
                         onClick={() => handleIconSelect(icon.emoji)}
                         className="w-8 h-8 flex items-center justify-center text-lg hover:bg-gray-100 rounded transition-colors"
                         title={`Select ${icon.name}`}
                       >
                         {icon.emoji}
                       </button>
                     ))}
                   </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-2">🔍</div>
                <p className="text-sm">No icons found matching "{iconSearchTerm}"</p>
                <p className="text-xs mt-1">Try searching for a different term</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 no-animations">
      <NavigationBar />
      
      <div className="lg:grid lg:grid-cols-[auto_1fr] lg:min-h-screen">
        <BusinessDashboardSidebar
          isSidebarOpen={isSidebarOpen}
          isSidebarCollapsed={isSidebarCollapsed}
          activeSection="services"
          onClose={() => setIsSidebarOpen(false)}
          onToggleCollapse={toggleSidebar}
          onSectionChange={handleSectionChange}
        />
        
                 {/* Main Content */}
         <div className="flex flex-col min-h-screen">
                       {/* Mobile Header */}
            <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setIsSidebarOpen(true)}
                  className="p-2 rounded-md hover:bg-gray-100"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <h1 className="text-lg font-semibold text-gray-900">Manage Service Categories</h1>
                <div className="w-10"></div> {/* Spacer for centering */}
              </div>
            </div>
                       {/* Page Content */}
            <div className="flex-1 p-4 md:p-8">
             <div className="space-y-6">
               {/* Breadcrumb Navigation */}
               <div className="mb-4">
                 <button
                   onClick={handleBackNavigation}
                   className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
                 >
                   <ArrowLeft className="w-5 h-5" />
                   <span>{getBackNavigationInfo().text}</span>
                 </button>
               </div>

               {/* Page Header */}
               <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                 <div>
                   <h2 className="hidden md:block text-xl md:text-2xl font-bold text-gray-900">Manage Service Categories</h2>
                   <p className="text-gray-600 mt-1">Create and manage service categories for your business</p>
                 </div>
               </div>

               {/* Content Grid - hide right card when no categories and not adding/editing */}
               <div className={`grid gap-8 ${categories.length > 0 || isAdding || editingCategory ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
                {/* Categories List - hide on mobile when adding new category (form shown below) */}
                <div className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-visible ${
                  isAdding && !editingCategory ? 'hidden lg:block' : ''
                }`}>
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg font-medium text-gray-900">Categories</h2>
                      <button
                        onClick={handleAddNew}
                        className="aacore-btn2-primary"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add New</span>
                      </button>
                    </div>
                  </div>

                                     <div className="p-6 overflow-visible">
                     {loading ? (
                       <div className="flex items-center justify-center py-8">
                         <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                       </div>
                                           ) : categories.length > 0 ? (
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleDragEnd}
                        >
                          <SortableContext
                            items={categories.map(cat => cat.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            <div className="space-y-3 overflow-visible">
                              {categories.map((category) => (
                                <SortableItem
                                  key={category.id}
                                  category={category}
                                  onEdit={handleEdit}
                                  onDelete={handleDeleteClick}
                                  onResetServiceIcons={handleResetServiceIconsClick}
                                />
                              ))}
                            </div>
                          </SortableContext>
                        </DndContext>
                      ) : (
                       <div className="text-center py-8 text-gray-500">
                         <div className="text-4xl mb-4">📋</div>
                         <h3 className="text-lg font-medium mb-2">No Categories Yet</h3>
                         <p className="text-sm">Create your first service category to get started.</p>
                       </div>
                     )}
                   </div>
                </div>

                                                  {/* Form - only show when there are categories or user is adding/editing */}
                 {(categories.length > 0 || isAdding || editingCategory) && (
                 <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${
                   categories.length > 0 && !isAdding && !editingCategory ? 'hidden lg:block' : ''
                 }`}>
                   {(isAdding || editingCategory) && (
                     <div className="p-6 border-b border-gray-200">
                       <h2 className="text-lg font-medium text-gray-900">
                         {editingCategory ? 'Edit Category' : 'Add New Category'}
                       </h2>
                     </div>
                   )}

                   <div className="p-6">
                    {(isAdding || editingCategory) ? (
                      <form onSubmit={handleSubmit} className="space-y-4">
                        {showSuccessMessage && (
                          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-green-600 text-sm">
                              {isAdding ? 'Category created successfully!' : 'Category updated successfully!'}
                            </p>
                          </div>
                        )}
                        {errors.general && (
                          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-red-600 text-sm">{errors.general}</p>
                          </div>
                        )}

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Category Name *
                          </label>
                          <input
                            ref={categoryNameInputRef}
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                              errors.name ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="Enter category name"
                            maxLength={50}
                          />
                          {errors.name && (
                            <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description
                          </label>
                          <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter category description"
                            rows={3}
                            maxLength={200}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Icon
                          </label>
                          {renderIconDropdown()}
                          <p className="text-xs text-gray-500 mt-1">
                            Choose an icon to represent this category
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Background Color
                          </label>
                          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-3">
                            {CATEGORY_BACKGROUND_COLORS.map((c) => {
                              const selected = formData.background_color === c.value;
                              return (
                                <button
                                  key={c.value}
                                  type="button"
                                  onClick={() => setFormData({ ...formData, background_color: c.value })}
                                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition-colors ${
                                    selected ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200 hover:border-gray-300'
                                  }`}
                                  aria-pressed={selected}
                                >
                                  <span
                                    className="h-5 w-5 rounded-md border border-black/10"
                                    style={{ backgroundColor: c.value }}
                                  />
                                  <span className="text-sm text-gray-700">{c.name}</span>
                                </button>
                              );
                            })}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Used as the calendar assignment block background for services in this category.
                          </p>
                        </div>

                        {/* Display Order field hidden per requirement; value still maintained internally */}

                                                 <div className="flex items-center space-x-3 pt-4">
                           <button
                             type="button"
                             onClick={handleCancel}
                             className="aacore-btn2-outline bg-gray-100 hover:bg-gray-200 flex-1 md:flex-none"
                           >
                             <X className="w-4 h-4" />
                             <span>Cancel</span>
                           </button>
                           <button
                             type="submit"
                             className="aacore-btn2-primary flex-1 md:flex-none"
                           >
                             {editingCategory ? (
                               <Save className="w-4 h-4" />
                             ) : (
                               <Plus className="w-4 h-4" />
                             )}
                             <span>{editingCategory ? 'Update' : 'Create'}</span>
                           </button>
                         </div>
                      </form>
                    ) : categories.length > 0 ? (
                      <div className="flex items-center justify-center h-64">
                        <div className="text-center text-gray-500">
                          <div className="text-6xl mb-4">📋</div>
                          <h3 className="text-lg font-medium mb-2">No Category Selected</h3>
                          <p className="text-sm">
                            Select a category from the list to edit, or click "Add New" to create a new category.
                          </p>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
                 )}
              </div>
             </div>
            </div>
          </div>
        </div>

       {/* Delete Confirmation Modal */}
       {showDeleteModal && categoryToDelete && (
         <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
           <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
             <div className="p-6">
               <div className="flex items-center space-x-3 mb-4">
                 <div className="flex-shrink-0">
                   <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                     <Trash2 className="w-6 h-6 text-red-600" />
                   </div>
                 </div>
                 <div>
                   <h3 className="text-lg font-medium text-gray-900">Delete Category</h3>
                   <p className="text-sm text-gray-500">This action cannot be undone.</p>
                 </div>
               </div>
               
               <div className="mb-6">
                 <p className="text-gray-700">
                   Are you sure you want to delete the category{' '}
                   <span className="font-medium text-gray-900">"{categoryToDelete.name}"</span>?
                 </p>
                 <p className="text-sm text-gray-500 mt-2">
                   This will permanently remove the category and all associated data.
                 </p>
               </div>
               
               <div className="flex items-center justify-end space-x-3">
                 <button
                   onClick={handleDeleteCancel}
                   className="aacore-btn2-outline bg-gray-100 hover:bg-gray-200"
                 >
                   <X className="w-4 h-4" />
                   <span>Cancel</span>
                 </button>
                 <button
                   onClick={handleDeleteConfirm}
                   className="aacore-btn2-danger"
                 >
                   <Trash2 className="w-4 h-4" />
                   <span>Delete Category</span>
                 </button>
               </div>
             </div>
           </div>
         </div>
       )}

       {/* Reset Service Icons Confirmation Modal */}
       {showResetIconsModal && categoryToResetIcons && (
         <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
           <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
             <div className="p-6">
               <div className="flex items-center space-x-3 mb-4">
                 <div className="flex-shrink-0">
                   <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                     <RotateCcw className="w-6 h-6 text-orange-600" />
                   </div>
                 </div>
                 <div>
                   <h3 className="text-lg font-medium text-gray-900">Reset Service Icons</h3>
                   <p className="text-sm text-gray-500">This will remove custom icons from services.</p>
                 </div>
               </div>
               
               <div className="mb-6">
                 <p className="text-gray-700">
                   Are you sure you want to reset icons for all services in the category{' '}
                   <span className="font-medium text-gray-900">"{categoryToResetIcons.name}"</span>?
                 </p>
                 <p className="text-sm text-gray-500 mt-2">
                   This will remove custom icons from all services in this category. Services will use the category icon instead.
                 </p>
                 <p className="text-sm text-gray-500 mt-2">
                   You can still change the service icons individually later.
                 </p>
               </div>
               
               <div className="flex items-center justify-end space-x-3">
                 <button
                   onClick={handleResetServiceIconsCancel}
                   className="aacore-btn2-outline bg-gray-100 hover:bg-gray-200"
                 >
                   <X className="w-4 h-4" />
                   <span>Cancel</span>
                 </button>
                 <button
                   onClick={handleResetServiceIconsConfirm}
                   className="aacore-btn2-primary"
                   style={{ backgroundColor: '#f97316', borderColor: '#f97316' }}
                   onMouseEnter={(e) => {
                     e.currentTarget.style.backgroundColor = '#ea580c';
                     e.currentTarget.style.borderColor = '#ea580c';
                   }}
                   onMouseLeave={(e) => {
                     e.currentTarget.style.backgroundColor = '#f97316';
                     e.currentTarget.style.borderColor = '#f97316';
                   }}
                 >
                   <RotateCcw className="w-4 h-4" />
                   <span>Reset Icons</span>
                 </button>
               </div>
             </div>
           </div>
         </div>
       )}

               {/* Toast Notification */}
        {toast && (
          <div className="fixed top-4 right-4 z-50">
            <div className={`px-6 py-4 rounded-lg shadow-lg max-w-sm transform  ${
              toast.type === 'success' 
                ? 'bg-green-500 text-white' 
                : 'bg-red-500 text-white'
            }`}>
              <div className="flex items-center justify-between">
                <span className="font-medium">{toast.message}</span>
                <button
                  onClick={() => setToast(null)}
                  className="ml-4 text-white hover:text-gray-200 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Custom Alert Modal */}
        <CustomAlertModal
          isOpen={alertModal.isOpen}
          onClose={closeAlertModal}
          title={alertModal.title}
          message={alertModal.message}
          type={alertModal.type}
        />
      </div>
    );
  };

export default ManageCategoriesPage;
